/**
 * V2 Profile — pure derivations and small HTML helpers. Split out of
 * profile.js on 2026-07-25 (856 lines, plan budget ~400). No fetching and no
 * mounting here: everything is a function of its arguments, which is why the
 * tests for this behaviour never needed the DOM. profile.js re-exports the
 * public ones, so importers are unaffected.
 */
import * as gateway from '../../core/gateway.js';
import { escapeHtml } from '../../../assets/js/utils.js';
import { icon } from '../../ui/icons.js';

/**
 * Percent-encode the characters that could break out of a CSS url('…')
 * inside a style attribute (quotes, parens, whitespace, backslash).
 * Targeted on purpose: encodeURI would double-encode %XX sequences that
 * storage URLs already contain.
 */
export function cssUrl(url) {
  return url.replace(/['"()\s\\]/g, (c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`);
}

/**
 * Shape-guard the near-trophy rows and compute places-to-bronze. Ties
 * share a DENSE_RANK (same math as get_user_trophies), so MATCHING the
 * bronze count already earns the trophy — the gap is the difference,
 * not difference+1. Closest first. Pure — exported for tests.
 */
export function deriveNearTrophies(rows) {
  return (rows || [])
    .filter((t) => t.category_name
      && Number.isFinite(Number(t.rank))
      && Number.isFinite(Number(t.location_count))
      && Number.isFinite(Number(t.podium_count)))
    .map((t) => ({
      categoryId: t.category_id,
      name: t.category_name,
      rank: Number(t.rank),
      locationCount: Number(t.location_count),
      gap: Math.max(1, Number(t.podium_count) - Number(t.location_count)),
    }))
    .sort((a, b) => a.gap - b.gap || a.rank - b.rank || a.name.localeCompare(b.name))
    .slice(0, 10);
}

/**
 * V1's journal landing (user-panel.js renderCategoryGrid): the yoinks
 * grouped into one stack per category — hero image from the newest
 * yoink with a photo, unique-place count, trophy badge — sorted by
 * place count descending. Exported for tests.
 */
export function deriveCategoryStacks(yoinks, trophies = []) {
  const byId = new Map();
  for (const y of yoinks) {
    const catId = y.category_id || y.categories?.id;
    if (!catId || !y.categories) continue;
    const entry = byId.get(catId) || {
      id: catId,
      name: y.categories.name,
      category: y.categories,
      places: new Set(),
      yoinks: 0,
      heroUrl: null,
    };
    entry.yoinks += 1;
    if (y.location_id) entry.places.add(y.location_id);
    if (!entry.heroUrl) {
      const images = Array.isArray(y.images) ? y.images : [];
      if (images.length && images[0]) {
        entry.heroUrl = images[0].startsWith('http')
          ? images[0]
          : gateway.getPublicUrl('yoinks', images[0]);
      }
    }
    byId.set(catId, entry);
  }
  const rankFor = new Map((trophies || []).map((t) => [t.category_id, t.rank]));
  return [...byId.values()]
    .map((entry) => ({ ...entry, places: entry.places.size, trophyRank: rankFor.get(entry.id) || null }))
    .sort((a, b) => b.places - a.places || a.name.localeCompare(b.name));
}

export const TROPHY_MEDALS = { 1: 'gold', 2: 'silver', 3: 'bronze' };

/** Rank 1-3 as a drawn ink medal in its metal's color, else ''. */
export function trophyMedal(rank, size = 15) {
  const metal = TROPHY_MEDALS[rank];
  if (!metal) return '';
  return `<span class="trophy-medal trophy-medal-${metal}" aria-hidden="true">${icon('medal', size)}</span>`;
}

/**
 * Count the case's medals by metal — the tally line ("3 gold · 1
 * silver") derives from the trophy rows already fetched, so it costs
 * nothing. Pure — exported for tests.
 */
export function medalTally(rows) {
  const tally = { gold: 0, silver: 0, bronze: 0 };
  for (const t of rows || []) {
    const metal = TROPHY_MEDALS[t?.rank];
    if (metal && t.category_name) tally[metal] += 1;
  }
  return tally;
}

/**
 * Shape-guard the trophy-gap rows (get_user_trophy_gaps: ranks 2-6,
 * each with its next-rung-up target) and compute the gap. Same tie
 * convention as deriveNearTrophies: dense ranks share, so MATCHING the
 * target count already earns that metal — gap is the difference.
 * Closest first; the view splits medal upgrades (rank 2-3) from podium
 * reach (rank 4-6). Pure — exported for tests.
 */
export function deriveTrophyGaps(rows) {
  return (rows || [])
    .filter((t) => t.category_name
      && Number.isFinite(Number(t.rank))
      && Number.isFinite(Number(t.location_count))
      && TROPHY_MEDALS[Number(t.target_rank)]
      && Number.isFinite(Number(t.target_count)))
    .map((t) => ({
      categoryId: t.category_id,
      name: t.category_name,
      rank: Number(t.rank),
      locationCount: Number(t.location_count),
      targetRank: Number(t.target_rank),
      gap: Math.max(1, Number(t.target_count) - Number(t.location_count)),
    }))
    .sort((a, b) => a.gap - b.gap || a.rank - b.rank || a.name.localeCompare(b.name));
}

/**
 * Shape-guard the medal-table rows (get_medal_table). The server
 * already orders Olympic-style and computes shared positions; the
 * client only drops rows that don't look like standings (the RPC shape
 * guard every optional RPC gets). Pure — exported for tests.
 */
export function deriveMedalTable(rows) {
  return (rows || [])
    .filter((r) => r?.username
      && Number.isFinite(Number(r.rank))
      && Number.isFinite(Number(r.gold))
      && Number.isFinite(Number(r.silver))
      && Number.isFinite(Number(r.bronze)))
    .map((r) => ({
      userId: r.user_id,
      username: r.username,
      name: r.full_name || r.username,
      rank: Number(r.rank),
      gold: Number(r.gold),
      silver: Number(r.silver),
      bronze: Number(r.bronze),
    }));
}

/**
 * Avatar/banner paths are deterministic ({id}/avatar.ext), so a
 * re-upload keeps the same URL and Safari serves the stale cached image
 * — V1's documented fix is a ?v={updated_at} cache-buster
 * (user-panel.js:452-458). Restored per M8.5 Tier 0.
 */
export function versionedStorageUrl(path, version) {
  if (path.startsWith('http')) return path;
  const url = gateway.getPublicUrl('avatars', path);
  if (!url) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}v=${encodeURIComponent(version || '')}`;
}

export function avatarHtml(profile) {
  if (profile.avatar_url) {
    const url = versionedStorageUrl(profile.avatar_url, profile.updated_at);
    return `<img class="profile-avatar" src="${url}" alt="">`;
  }
  const initial = (profile.full_name || profile.username || '?').charAt(0).toUpperCase();
  return `<div class="profile-avatar profile-avatar-blank" aria-hidden="true">${escapeHtml(initial)}</div>`;
}

/**
 * V1's social-link rules (user-panel.js:821-841): a schemeless website
 * still links (https:// prefixed), the label is the bare host+path (no
 * scheme, no trailing slash), and handles display with @ but link
 * without. Exported for tests.
 */
export function socialLinks(profile) {
  const links = [];
  if (profile.website) {
    const href = profile.website.startsWith('http') ? profile.website : `https://${profile.website}`;
    const label = profile.website.replace(/^https?:\/\//, '').replace(/\/$/, '');
    links.push(`<a href="${escapeHtml(href)}" target="_blank" rel="noopener">${icon('globe', 14)} ${escapeHtml(label)}</a>`);
  }
  if (profile.instagram) {
    const handle = profile.instagram.replace(/^@/, '');
    links.push(`<a href="https://instagram.com/${escapeHtml(handle)}" target="_blank" rel="noopener">${icon('camera', 14)} @${escapeHtml(handle)}</a>`);
  }
  if (profile.twitter) {
    const handle = profile.twitter.replace(/^@/, '');
    links.push(`<a href="https://x.com/${escapeHtml(handle)}" target="_blank" rel="noopener">𝕏 @${escapeHtml(handle)}</a>`);
  }
  return links.length ? `<div class="profile-links">${links.join(' · ')}</div>` : '';
}
